/**
 * Which state's rules apply to this member.
 *
 * A SEPARATE FIELD FROM THE ADDRESS STATE, ON PURPOSE.
 * `members.state` is free text written by a dozen import paths and filled in on
 * a small minority of rows. Benefit rules are state-specific, and a wrong code
 * does not fail — it answers with another state's regulations, which is worse
 * than answering nothing. So this is set deliberately, the free-text value is
 * shown only as a hint, and the field offers the states we actually hold rules
 * for rather than all fifty.
 */

import React, { useEffect, useState } from 'react';
import { Loader2, MapPin, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { benefitsApi, type StateCodeResult } from './types';

interface Props {
  memberId: number;
  onChanged?: (stateCode: string | null) => void;
  /** Read-only when the caller has no write permission for this member. */
  readOnly?: boolean;
}

export const MemberStateCodeField: React.FC<Props> = ({ memberId, onChanged, readOnly }) => {
  const [data, setData] = useState<StateCodeResult | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    benefitsApi
      .getStateCode(memberId)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setDraft(d.state_code ?? '');
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Could not load.'));
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const next = await benefitsApi.setStateCode(memberId, draft || null);
      setData(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
      onChanged?.(next.state_code);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (error && !data) {
    return <p className="text-sm text-destructive">{error}</p>;
  }
  if (!data) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
      </p>
    );
  }

  const dirty = (draft || null) !== data.state_code;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <Label htmlFor="benefit-state-code" className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        Benefit rules state
      </Label>
      <p className="mt-1 text-xs text-muted-foreground">
        Benefit rules differ by state, so this decides which set applies. It is set here rather
        than taken from the address, because an address written by an import is not reliable
        enough to answer a mother with.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {/* A native select: it takes `id` and `disabled`, which the shared
            Select primitive does not, and it does not lose pointer events
            inside a dialog. Eight options do not need a combobox. */}
        <select
          id="benefit-state-code"
          className="h-9 w-44 rounded-md border border-input bg-background px-3 text-sm text-foreground disabled:opacity-60"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={readOnly || saving}
        >
          <option value="">Not set</option>
          {data.states_with_rules.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>

        {!readOnly && (
          <Button size="sm" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : saved ? (
              <Check className="mr-1 h-4 w-4" aria-hidden="true" />
            ) : null}
            {saved ? 'Saved' : 'Save'}
          </Button>
        )}
      </div>

      {data.state_free_text && (
        <p className="mt-2 text-xs text-muted-foreground">
          Address on file says “{data.state_free_text}”. Shown for context only — it does not set
          this field.
        </p>
      )}

      {data.states_with_rules.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          No state has benefit rules loaded yet.
        </p>
      )}

      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default MemberStateCodeField;
